import { useState, useEffect } from "react";
import { Clock, DollarSign, Plus, Save, X, Moon } from "lucide-react";
import { computeDuration, fmtHours, fmtMoney, isOvernight } from "@/lib/calc";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function EntryForm({ settings, projects, onSubmit, editing, onCancelEdit }) {
  const [date, setDate] = useState(todayStr());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [project, setProject] = useState(projects[0] || "General");
  const [rate, setRate] = useState(settings.hourly_rate);
  const [multiplier, setMultiplier] = useState(settings.multiplier);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setDate(editing.date);
      setStart(editing.start_time);
      setEnd(editing.end_time);
      setProject(editing.project);
      setRate(editing.hourly_rate);
      setMultiplier(editing.multiplier);
      setNotes(editing.notes || "");
    }
  }, [editing]);

  const duration = computeDuration(start, end);
  const earnings = duration * Number(rate || 0) * Number(multiplier || 0);
  const overnight = isOvernight(start, end);

  const reset = () => {
    setDate(todayStr());
    setStart("");
    setEnd("");
    setProject(projects[0] || "General");
    setRate(settings.hourly_rate);
    setMultiplier(settings.multiplier);
    setNotes("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        date,
        start_time: start,
        end_time: end,
        project,
        notes,
        hourly_rate: Number(rate),
        multiplier: Number(multiplier),
      });
      if (!editing) reset();
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-[#14161c] border border-[#2b2f3d] rounded-lg px-3 py-2.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors font-mono-data text-sm";
  const labelCls = "font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="entry-form"
      className="bg-[#181a22] border border-[#2b2f3d] rounded-xl p-5 lg:sticky lg:top-24"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#f3f4f8]">
          {editing ? "Edit Entry" : "Punch Clock"}
        </h3>
        {editing && (
          <button type="button" onClick={onCancelEdit} data-testid="entry-form-cancel-button" className="text-[#62697e] hover:text-red-400 transition-colors">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelCls}>Date</label>
          <input data-testid="entry-form-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`mt-1.5 ${inputCls}`} required />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Start</label>
            <input data-testid="entry-form-start-time" type="time" value={start} onChange={(e) => setStart(e.target.value)} className={`mt-1.5 ${inputCls}`} required />
          </div>
          <div>
            <label className={labelCls}>End</label>
            <input data-testid="entry-form-end-time" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={`mt-1.5 ${inputCls}`} required />
          </div>
        </div>

        <div>
          <label className={labelCls}>Project / Category</label>
          <Select value={project} onValueChange={setProject}>
            <SelectTrigger data-testid="entry-form-project-select" className="mt-1.5 bg-[#14161c] border-[#2b2f3d] text-[#f3f4f8] font-mono-data text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181a22] border-[#2b2f3d] text-[#f3f4f8]">
              {projects.map((p) => (
                <SelectItem key={p} value={p} className="font-mono-data text-sm">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Rate / hr</label>
            <input data-testid="entry-form-rate-input" type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className={`mt-1.5 ${inputCls}`} required />
          </div>
          <div>
            <label className={labelCls}>Multiplier</label>
            <input data-testid="entry-form-multiplier-input" type="number" step="0.1" min="0" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} className={`mt-1.5 ${inputCls}`} required />
          </div>
        </div>

        <div>
          <label className={labelCls}>Notes</label>
          <textarea data-testid="entry-form-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`mt-1.5 ${inputCls} resize-none`} placeholder="What did you work on?" />
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="bg-[#14161c] border border-[#2b2f3d] rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[#62697e]"><Clock className="w-3.5 h-3.5" /><span className="font-mono-data text-[10px] uppercase tracking-[0.18em]">Duration</span></div>
          <div data-testid="entry-live-duration-preview" className="mt-1 font-mono-data font-bold text-xl text-amber-500">{fmtHours(duration)}</div>
          {overnight && <div className="mt-1 flex items-center gap-1 text-[10px] text-[#f97316] font-mono-data"><Moon className="w-3 h-3" /> Overnight</div>}
        </div>
        <div className="bg-[#14161c] border border-[#2b2f3d] rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[#62697e]"><DollarSign className="w-3.5 h-3.5" /><span className="font-mono-data text-[10px] uppercase tracking-[0.18em]">Payout</span></div>
          <div data-testid="entry-live-earnings-preview" className="mt-1 font-mono-data font-bold text-xl text-emerald-500">{fmtMoney(earnings, settings.currency)}</div>
        </div>
      </div>

      <button
        data-testid="entry-form-submit-button"
        type="submit"
        disabled={saving}
        className="mt-5 w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0c0d10] font-display font-bold uppercase tracking-wide rounded-lg px-4 py-3 flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
      >
        {editing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        {saving ? "Saving..." : editing ? "Save Changes" : "Add Entry"}
      </button>
    </form>
  );
}
